import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { UomType } from '../../../app/services/api/orderManagementClient';
import UomTypeItemRow from '../../../pages/UomTypes/components/UomTypeItemRow';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';

type Props = {
  list: UomType[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const UomTypesList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('uomTypes');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('uomTypes')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        addItemText={t('createNew')}
        onAddIsCreating={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('uomTypes')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('uomTypes')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
        />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('uomName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('instanceName')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('uomCode')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('isMeasurement')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('isBase')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('isSystemUom')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.length > 0 ? (
                <>
                  {list.map((uomType) => (
                    <UomTypeItemRow
                      key={uomType.uomTypeId}
                      uomType={uomType}
                    />
                  ))}
                  <EmptyRows
                    numberOfCurrentRows={list.length}
                    height={40}
                  />
                </>
              ) : (
                <TableRow>
                  <TableCell 
                    colSpan={6} 
                    sx={{ textAlign: 'center', py: 4 }}
                  >
                    {t('noUomTypesFound')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default UomTypesList;
